// src/pages/OnboardingPage.js
// Updated: 10/20/2025 08:45 PM (Added checks for Cloud Function result data)

import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase/config';
import { functions as firebaseFunctions } from '../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { doc, writeBatch, serverTimestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import Spinner from '../components/common/Spinner';

// Initialize the callable function
const joinFamilyCallable = httpsCallable(firebaseFunctions, 'joinFamily');

const OnboardingPage = () => {
  const [view, setView] = useState('main');
  const [familyName, setFamilyName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const { currentUser, refreshUserData } = useAuth();
  const navigate = useNavigate();

  const handleCreateFamily = async (e) => {
    e.preventDefault();
    if (!familyName.trim()) { toast.error('Please enter a family name.'); return; }
    setLoading(true);
    try {
      const familyId = uuidv4();
      const familyDocRef = doc(db, 'families', familyId);
      const userDocRef = doc(db, 'users', currentUser.uid);
      const batch = writeBatch(db);
      batch.set(familyDocRef, { familyName: familyName, inviteCode: uuidv4().substring(0, 8).toUpperCase(), hosts: [currentUser.uid], members: [currentUser.uid], createdAt: serverTimestamp() });
      batch.update(userDocRef, { familyId: familyId });
      await batch.commit();
      await refreshUserData(); // Refresh user data *after* successful commit
      toast.success(`'${familyName}' family created!`);
      navigate('/'); // Navigate after success
    } catch (error) {
        toast.error('Failed to create family. Please try again.');
        console.error("Error creating family:", error); // Log error
    }
    finally { setLoading(false); }
  };

  // UPDATED: Added checks for result.data
  const handleJoinFamily = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) { toast.error('Please enter an invite code.'); return; }
    setLoading(true);

    try {
      // Call the Cloud Function with the invite code
      const result = await joinFamilyCallable({ inviteCode: inviteCode.trim() }); //

      // --- ADDED CHECK ---
      // Ensure result and result.data exist before proceeding
      if (result?.data?.success && result.data.familyName) {
          // Refresh user data on the client side AFTER successful function call
          await refreshUserData(); //

          // Use the familyName from the result
          toast.success(`Welcome to the ${result.data.familyName} family!`); //
          navigate('/'); //
      } else {
           // Handle unexpected success response structure
           console.error("Join family function returned success, but data is missing:", result?.data);
           toast.error('Failed to join family. Unexpected response from server.');
      }
      // --- END CHECK ---

    } catch (error) {
      // Display the specific error message from the HttpsError
      toast.error(error.message || 'Failed to join family. Please check the code and try again.'); //
      console.error("Error joining family:", error); //
    } finally {
      setLoading(false); //
    }
  };

  const renderMainView = () => (
    <>
      <h2 className="text-2xl font-bold text-center text-neutral-gray">Welcome!</h2><p className="text-center text-gray-600 mt-2">Let's get you connected with your family.</p>
      <div className="mt-8 space-y-4"><button onClick={() => setView('create')} className="w-full text-lg text-white font-semibold bg-primary hover:bg-blue-700 py-3 rounded-lg shadow-md transition-transform transform hover:scale-105">Create a New Family Group</button><button onClick={() => setView('join')} className="w-full text-lg text-primary font-semibold bg-white border-2 border-primary hover:bg-blue-50 py-3 rounded-lg shadow-md transition-transform transform hover:scale-105">Join an Existing Group</button></div>
    </>
  ); //

  const renderCreateView = () => (
    <>
      <h2 className="text-2xl font-bold text-center">Create Your Family Group</h2><p className="text-center text-gray-600 mt-2">Give your family a name, like "The Huckleberrys".</p>
      <form onSubmit={handleCreateFamily} className="mt-8 space-y-4"><div><label htmlFor="familyName" className="sr-only">Family Name</label><input id="familyName" type="text" value={familyName} onChange={(e) => setFamilyName(e.target.value)} placeholder="e.g., The Hudson Family" required className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"/></div><div className="flex items-center gap-4"><button type="button" onClick={() => setView('main')} className="w-1/3 text-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">Back</button><button type="submit" disabled={loading} className="w-2/3 flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-cta-orange hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cta-orange disabled:opacity-50">{loading ? <Spinner size="sm" /> : 'Create Family'}</button></div></form>
    </>
  ); //

  const renderJoinView = () => (
    <>
      <h2 className="text-2xl font-bold text-center">Join a Family Group</h2><p className="text-center text-gray-600 mt-2">Enter the unique invite code provided by your family host.</p>
      <form onSubmit={handleJoinFamily} className="mt-8 space-y-4"><div><label htmlFor="inviteCode" className="sr-only">Invite Code</label><input id="inviteCode" type="text" value={inviteCode} onChange={(e) => setInviteCode(e.target.value)} placeholder="Enter Invite Code" required className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"/></div><div className="flex items-center gap-4"><button type="button" onClick={() => setView('main')} className="w-1/3 text-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">Back</button><button type="submit" disabled={loading} className="w-2/3 flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-success hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-success disabled:opacity-50">{loading ? <Spinner size="sm" /> : 'Join Family'}</button></div></form>
    </>
  ); //

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center px-4"><div className="max-w-md w-full bg-white p-8 rounded-xl shadow-lg">{view === 'main' && renderMainView()}{view === 'create' && renderCreateView()}{view === 'join' && renderJoinView()}</div></div>
  ); //
};

export default OnboardingPage;